import { useState, useRef } from 'react';
import { Upload, File, X, Loader2, CheckCircle, ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * DocumentUpload Component
 * Single document upload with preview and status display
 */
export function DocumentUpload({
  label,
  documentType,
  currentFile,
  onFileSelect,
  onUpload,
  onDelete,
  disabled = false,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
  maxSize = 10 * 1024 * 1024, // 10MB
  className,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Get file extension from URL
  const getFileExtension = (url) => {
    if (!url) return '';
    const parts = url.split('.');
    return parts[parts.length - 1].toUpperCase();
  };

  // Get file icon based on extension
  const getFileIcon = (ext) => {
    return <File className="h-5 w-5" />;
  };

  // Validate file
  const validateFile = (file) => {
    if (file.size > maxSize) {
      return `File size must be less than ${maxSize / (1024 * 1024)}MB`;
    }
    return null;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSelectedFile(file);

    if (onFileSelect) {
      onFileSelect(file, documentType);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !onUpload || isUploading) return;

    setIsUploading(true);
    setError(null);

    try {
      await onUpload(selectedFile, documentType);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!onDelete || isDeleting) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(documentType);
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  // Clear selected file
  const handleClear = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const hasCurrentFile = !!currentFile;
  const hasSelectedFile = !!selectedFile;

  return (
    <div className={cn('space-y-2', className)}>
      <label className="text-sm font-medium text-gray-700">{label}</label>

      <div
        className={cn(
          'border rounded-lg p-4 transition-colors',
          hasCurrentFile && 'border-green-200 bg-green-50',
          !hasCurrentFile && !hasSelectedFile && 'border-dashed border-gray-300',
          hasSelectedFile && !hasCurrentFile && 'border-blue-200 bg-blue-50',
          disabled && 'opacity-50'
        )}
      >
        {/* Current uploaded file */}
        {hasCurrentFile && !hasSelectedFile && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {label} uploaded
                </p>
                <p className="text-xs text-gray-500">
                  {getFileExtension(currentFile)} file
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => window.open(currentFile, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={triggerFileInput}
                disabled={disabled}
              >
                Replace
              </Button>
              {onDelete && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDelete}
                  disabled={disabled || isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Selected file (pending upload) */}
        {hasSelectedFile && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                {getFileIcon(selectedFile.name)}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onUpload && (
                <Button
                  size="sm"
                  onClick={handleUpload}
                  disabled={isUploading || disabled}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </>
                  )}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleClear}
                disabled={isUploading || disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Upload area (no file) */}
        {!hasCurrentFile && !hasSelectedFile && (
          <div
            className={cn(
              'flex flex-col items-center justify-center py-4 cursor-pointer',
              disabled && 'cursor-not-allowed'
            )}
            onClick={triggerFileInput}
          >
            <Upload className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-400 mt-1">
              PDF, DOC, DOCX, JPG, PNG (max {maxSize / (1024 * 1024)}MB)
            </p>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}

export default DocumentUpload;
